/** Preconfigured Managers group. Same id as the old workspace Manager role. */
export const PRECONFIGURED_MANAGER_GROUP_ID = "88888888-8888-8888-8888-888888888888";

/** Running work/pause shorter than this is discarded instead of saved. */
export const MIN_RECORD_SAVE_SECONDS = 60;

/** Name the API gives pauses it inserts to satisfy break rules on start. */
export const AUTO_BREAK_NAME = "Automatically generated break";

export interface WorkingTimeTracking {
	readonly allHoursEnabled?: boolean | null;
	readonly projecthoursEnabled?: boolean | null;
	readonly workinghoursEnabled?: boolean | null;
	readonly supervisorViews?: {
		readonly createAbsences?: boolean | null;
		readonly createWorkinghours?: boolean | null;
	} | null;
}

/** Working hours on, project hours off. Combined / project-only stay on the old supervisor create path. */
export const isWorkingHoursOnlyWorkspace = (settings: WorkingTimeTracking | null | undefined): boolean =>
	settings?.workinghoursEnabled === true && settings?.projecthoursEnabled !== true;

/** Missing flags stay on so existing workspaces keep today's supervisor create behavior. */
export const supervisorMayCreateForDirectReports = (
	settings: WorkingTimeTracking | null | undefined,
	kind: "workinghours" | "absences"
): boolean => {
	if (!isWorkingHoursOnlyWorkspace(settings)) {
		return true;
	}
	const views = settings?.supervisorViews;
	if (kind === "absences") {
		return views?.createAbsences !== false;
	}
	return views?.createWorkinghours !== false;
};

export const workinghoursWriteDenyMessage = (input: {
	readonly action: "add" | "update" | "delete";
	readonly actorId: string;
	readonly isSupervisorOfTarget: boolean;
	readonly settings: WorkingTimeTracking | null | undefined;
	readonly targetUserId: string;
	readonly type?: string | null;
}): string => {
	if (input.action !== "add") {
		return input.actorId !== input.targetUserId
			? "errors.times.workinghours.only_own_times"
			: "errors.times.workinghours.missing_edit";
	}
	const absence = input.type === "absence";
	if (input.actorId === input.targetUserId) {
		return absence ? "errors.times.workinghours.missing_create_absence" : "errors.times.workinghours.missing_create";
	}
	if (input.isSupervisorOfTarget) {
		const mayWork = supervisorMayCreateForDirectReports(input.settings, "workinghours");
		const mayAbsence = supervisorMayCreateForDirectReports(input.settings, "absences");
		if (absence && !mayAbsence) {
			return "errors.times.workinghours.missing_create_absence";
		}
		if (absence && !mayWork) {
			return "errors.times.workinghours.missing_create_absence_as_work";
		}
		if (!absence && !mayWork) {
			return "errors.times.workinghours.missing_create";
		}
	}
	return "errors.times.workinghours.only_own_times";
};

/** Working times exist only because project times generate them. */
export const workingTimesGeneratedFromProjectTimes = (settings: WorkingTimeTracking | null | undefined): boolean =>
	settings?.workinghoursEnabled === true && settings.projecthoursEnabled === true && settings.allHoursEnabled === true;

export interface ManageOtherWorkingTimesInput {
	readonly isAdmin: boolean;
	readonly isInGroup: (groupId: string) => boolean;
	readonly manageAllIds: readonly string[];
	readonly settings: WorkingTimeTracking | null | undefined;
}

/** Stored list wins. Generated working times cannot be managed manually, including by an admin. */
export const canManageOtherUsersWorkingTimes = (input: ManageOtherWorkingTimesInput): boolean => {
	if (workingTimesGeneratedFromProjectTimes(input.settings)) {
		return false;
	}
	if (input.isAdmin) {
		return true;
	}
	return input.manageAllIds.some((id) => input.isInGroup(id));
};

export interface EditWorkingTimeTargetInput extends ManageOtherWorkingTimesInput {
	readonly canEditOwn: boolean;
	readonly isSelf: boolean;
	readonly isSupervisor: boolean;
}

/** Own times need the edit-own right. Generated working times are not edited by hand, including by an admin. */
export const canEditWorkingTimeTarget = (input: EditWorkingTimeTargetInput): boolean => {
	if (workingTimesGeneratedFromProjectTimes(input.settings)) {
		return false;
	}
	if (!input.canEditOwn) {
		return false;
	}
	if (input.isSelf) {
		return true;
	}
	return input.isSupervisor || canManageOtherUsersWorkingTimes(input);
};
